import { take } from "lodash";
import pluralize from "pluralize";
import { useMemo, useState } from "react";
import { AnswerCitationNode } from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentOrganizationVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button, { ButtonSize, buttonTheme } from "@components/button/button";
import MotionDiv from "@components/motion/motion-div";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";

import { assessmentContextSidebarVar } from "./context-sidebar/context-sidebar";

const MAX_CHUNK_SIZE = 10;

const AssessmentQuestionStackedTextAnswers = ({
  answers,
  questionId,
  viewMoreEnabled,
  showContextSidebarCTA,
}: {
  questionId: number;
  answers: {
    responder?: BasicUser | null;
    text?: string | null;
    answerValue?: string | null;
    answerId?: number | null;
    inferredSources?: {
      edges: ReadonlyArray<{
        node?: AnswerCitationNode | null;
      } | null>;
    } | null;
  }[];
  showContextSidebarCTA?: boolean;
  viewMoreEnabled?: boolean;
}) => {
  const { saveUiPreference } = useUiPreferenceCache();
  const organization = currentOrganizationVar();
  const [answerLimit, setAnswerLimit] = useState(MAX_CHUNK_SIZE);

  const answerData = useMemo(() => {
    const data = viewMoreEnabled ? take(answers, answerLimit) : answers;
    return data.filter((answer) => answer.text);
  }, [answers, answerLimit, viewMoreEnabled]);

  return (
    <div className="flex flex-col gap-6">
      {answerData.map((answer, index) => {
        const sourceCount = answer.inferredSources?.edges?.length || 0;
        const responderId = answer.responder?.id;
        return (
          <MotionDiv
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            key={index}
            className="text-sm"
          >
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                <Avatar className="mr-2" user={answer.responder} size="5" />
                <div className="text-ellipsis font-medium text-gray-600 overflow-hidden text-nowrap">
                  {answer.responder ? answer.responder.name : "Anonymous"}
                </div>
              </div>
              {answer.answerValue && (
                <div className="text-xs text-gray-500">
                  (Answer: {answer.answerValue})
                </div>
              )}
            </div>
            <div className="mt-2">
              <TextareaWysiwyg
                className="max-w-full"
                editable={false}
                value={answer.text}
              />
            </div>
            {showContextSidebarCTA === true &&
              organization.featureFlags.reviewContextGeneration &&
              waffle.flag_is_active("review-context-sidebar") &&
              responderId &&
              sourceCount > 0 && (
                <div className="mt-2 hidden lg:flex">
                  <Button
                    onClick={() => {
                      saveUiPreference({ assessmentSidebarExpanded: true });
                      assessmentContextSidebarVar({
                        questionId: questionId,
                        answerId: answer.answerId,
                        showSuggestedResponses: false,
                      });
                    }}
                    size={ButtonSize.medium}
                    theme={buttonTheme.lightBlue}
                    text={`${sourceCount} ${pluralize("Sources", sourceCount)}`}
                  />
                </div>
              )}
          </MotionDiv>
        );
      })}
      {viewMoreEnabled && answerLimit < answers.length && (
        <div className="mt-6">
          <Button
            theme={buttonTheme.lightGray}
            onClick={() => setAnswerLimit(answerLimit + MAX_CHUNK_SIZE)}
            text="View More"
          />
        </div>
      )}
    </div>
  );
};

export default AssessmentQuestionStackedTextAnswers;
